import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStatus } from "../../lib/queries";

function LoadingScreen() {
  return (
    <div className="ma-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", letterSpacing: "0.16em" }}>
        loading…
      </span>
    </div>
  );
}

const HAS_VISITED_KEY = "myams.hasVisited";

function hasVisited(): boolean {
  if (typeof localStorage === "undefined") return true;
  try { return localStorage.getItem(HAS_VISITED_KEY) === "1"; } catch { return true; }
}
export function markVisited(): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(HAS_VISITED_KEY, "1"); } catch { /* ignore */ }
}

/** Decides where to send the user on root `/`.
 *   - Never visited before → /welcome (shown once)
 *   - Returning visitor or already signed in → /discover
 * Auth is OPTIONAL; the welcome flow is just first-time guidance. */
export function RootRedirect() {
  return <Navigate to={hasVisited() ? "/discover" : "/welcome"} replace />;
}

/** Wraps the onboarding/login flow. If already authed, kick to /discover. */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { status } = useAuthStatus();
  if (status === "loading") return <LoadingScreen />;
  if (status === "authed") return <Navigate to="/discover" replace />;
  return <>{children}</>;
}

/** Wraps screens that need a user. Anon → /login with returnTo. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuthStatus();
  const loc = useLocation();
  if (status === "loading") return <LoadingScreen />;
  if (status === "anon") {
    return <Navigate to="/login" replace state={{ returnTo: loc.pathname + loc.search }} />;
  }
  return <>{children}</>;
}

/** Wraps `/admin/*` screens. Anon → /login with returnTo; user (non-admin)
 *  → /discover. Admins fall through. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { status, user } = useAuthStatus();
  const loc = useLocation();
  if (status === "loading") return <LoadingScreen />;
  if (status === "anon") {
    return <Navigate to="/login" replace state={{ returnTo: loc.pathname + loc.search }} />;
  }
  if (user?.role !== "admin") return <Navigate to="/discover" replace />;
  return <>{children}</>;
}
